import { formatDateShortDD, parseDayjs } from '~/services/TimeService';

export type RecordLike = Record<string, unknown>;

export type ChecklistStatus = 'approved' | 'requires approval' | 'blocked';
export type MarginBand = 'red' | 'yellow' | 'green';
export type ChecklistDetail = {
  label: string;
  value: string;
};

export function getStatusLabel(status: ChecklistStatus) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'requires approval':
      return 'Requires approval';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Unknown';
  }
}

export function formatOptionalNumber(value?: number) {
  return isFiniteNumber(value) ? String(value) : 'N/A';
}

export function formatMoney(value?: number | null, currencyCode = 'AUD') {
  return isFiniteNumber(value) ? `${currencyCode} ${value}` : 'N/A';
}

export function formatPrice(value: string | number | null | undefined, currencyCode = 'AUD') {
  if (value == null || value === '') {
    return '—';
  }
  return `${currencyCode} ${value}`;
}

export function formatContextDate(value?: string) {
  return value ? formatDateShortDD(value) : 'Not available';
}

export function formatBooleanDataPointValue({
  dataAvailable,
  enabled,
  unavailableValue = 'N/A',
}: {
  dataAvailable: boolean;
  enabled?: boolean;
  unavailableValue?: string;
}) {
  if (!dataAvailable) {
    return unavailableValue;
  }
  return enabled ? 'Yes' : 'No';
}

export function formatLoadedDataPointValue({ dataAvailable, loaded }: { dataAvailable: boolean; loaded: boolean }) {
  if (!dataAvailable) {
    return 'N/A';
  }
  return loaded ? 'Loaded' : 'Not loaded';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function normalizeString(value?: string | null) {
  return value?.trim() || undefined;
}

export function normalizeComparisonText(value: string) {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

export function parseChecklistDate(value?: string | null) {
  return value ? parseDayjs(value) : undefined;
}

export function parseDate(value?: string | null) {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function readString(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];

    if (typeof fieldValue === 'string' && fieldValue.trim()) {
      return fieldValue;
    }
  }

  return undefined;
}

export function readNumber(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];
    const numberValue = typeof fieldValue === 'string' && fieldValue.trim() !== '' ? Number(fieldValue) : fieldValue;

    if (isFiniteNumber(numberValue)) {
      return numberValue;
    }
  }

  return undefined;
}

export function readBoolean(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const value = record[field];

    if (typeof value === 'boolean') {
      return value;
    }
  }

  return undefined;
}

export function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}
